import * as d3 from 'd3';
import { Component, h } from 'preact';

import { showPrecinctsReporting } from '../endpoints';

import { customNumFormat, customBarNumFormat } from '../utils/format-numbers';

class MiniBarChart extends Component {
  constructor() {
    super();

    this.state = {
      width: null,
      height: 90,
      bodyWidth: null,
      breakPoint: 768,
    };

    this.createGraphic = this.createGraphic.bind(this);
    this.redrawGraphic = this.redrawGraphic.bind(this);
    this.getContainerWidth = this.getContainerWidth.bind(this);
  }

  componentDidMount() {
    const {
      className,
      categoryNumbers,
      candidatesRender,
      candidates,
      officeSlug,
      party,
      maxNum,
    } = this.props;
    let { width, bodyWidth } = this.getContainerWidth(officeSlug);

    this.setState({ width: width, bodyWidth: bodyWidth }, () => {
      this.createGraphic(
        className,
        categoryNumbers,
        candidatesRender,
        candidates,
        officeSlug,
        party,
        maxNum
      );
    });

    window.addEventListener('resize', () => {
      this.redrawGraphic();
    });
  }

  componentDidUpdate() {
    const {
      className,
      categoryNumbers,
      candidatesRender,
      candidates,
      officeSlug,
      party,
      maxNum,
    } = this.props;

    this.createGraphic(
      className,
      categoryNumbers,
      candidatesRender,
      candidates,
      officeSlug,
      party,
      maxNum
    );
  }

  createGraphic(
    className,
    categoryNumbers,
    candidatesRender,
    candidates,
    officeSlug,
    party,
    maxNum
  ) {
    let { width, bodyWidth } = this.getContainerWidth(officeSlug);

    let { height, breakPoint } = this.state;

    const partySlug = party ? party.toLowerCase() : '';
    const chartSelector = `.bar-chart-${className}-${officeSlug}-${partySlug}`;

    d3.selectAll(`${chartSelector} svg`).remove();

    const container = d3.select(chartSelector);

    // chart setup
    let margin = { top: 0, bottom: 20, right: 60, left: 70 };

    width = width - margin.left - margin.right;
    height = 68 - margin.top - margin.bottom;

    const isMobile = bodyWidth < breakPoint;

    let svg = container
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);

    let diagonalStripePattern = svg
      .append('defs')
      .append('pattern')
      .attr('id', 'diagonal-stripe')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(45 0 0)')
      .attr('width', 6)
      .attr('height', 6);

    diagonalStripePattern
      .append('rect')
      .attr('width', 6)
      .attr('height', 6)
      .style('fill', '#d9d9d9');

    diagonalStripePattern
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 6)
      .style('stroke', 'black')
      .style('stroke-width', 0.5);

    let g = svg
      .append('g')
      .attr('class', `bar-chart-${className}-${officeSlug}-${partySlug}-group`)
      .attr('width', width)
      .attr('transform', `translate(${margin.left},${margin.top})`);

    let newcand = candidates.map((obj) => ({
      ...obj,
      last_name: obj.name.split(' ').slice(-1).join(' '),
      category_votes: categoryNumbers[1][0][obj.id],
    }));

    newcand = newcand.sort(function (a, b) {
      return (b.category_votes || 0) - (a.category_votes || 0);
    });

    // Use a reasonable alt max for primary elections
    let altMaxNum = 600000;

    const domainMax = isNaN(maxNum) ? altMaxNum : Math.max(maxNum, altMaxNum);

    var x = d3.scaleLinear().range([0, width]).domain([0, domainMax]);

    var y = d3
      .scaleBand()
      .domain(
        newcand.map(function (entry) {
          return entry.last_name;
        })
      )
      .range([height, 0])
      .padding(0.4);

    // light gray bar underneath
    g
      .selectAll('.bar-gray')
      .data(newcand)
      .enter()
      .append('rect')
      .attr('class', 'bar-light-gray')
      .attr('fill', '#F5F5F5')
      .attr('width', x(domainMax))
      .attr('y', (d) => y(d.last_name))
      .attr('height', y.bandwidth());

    // add the x Axis
    g.append('g')
      .attr('transform', 'translate(0,' + height + ')')
      .call(
        d3.axisBottom(x).tickValues([300000, 600000]).tickFormat(d => d3.format('~s')(d).toUpperCase()).tickSize(-height)
      )
      .attr('class', 'axis axis--x')
      .selectAll('text')
      .attr('dy', '0.99em');

    // add the y Axis
    g.append('g')
      .attr('class', 'axis axis--y')
      .style('text-anchor', 'end')
      .call(d3.axisLeft(y))
      .selectAll('text')
      .attr('y', 0)
      .attr('dy', '.35em')
      .attr('x', -10)
      .attr('text-anchor', 'end');

    // append the rectangles for the bar chart
    g
      .selectAll('.bar')
      .data(newcand)
      .enter()
      .append('rect')
      .attr('class', 'bar-category')
      .attr('fill', (d) => {
        let foundCandidate = candidatesRender.find((a) => a.id == d.id);
        if (foundCandidate) return foundCandidate.color;
        else return '#bdbdbd';
      })
      .attr('width', (d) => x(d.category_votes || 0))
      .attr('y', (d) => y(d.last_name))
      .attr('height', y.bandwidth());

    g.selectAll('.bar')
      .data(newcand)
      .enter()
      .append('text')
      .attr('x', (d) => x(d.category_votes || 0) + 3)
      .attr('text-anchor', 'start')
      .attr('y', (d) => y(d.last_name) + y.bandwidth() / 2)
      .attr('dy', '.35em')
      .text((d) => customBarNumFormat(d.category_votes || 0));
  }

  getContainerWidth(office_slug) {
    const body = d3.select('body');
    const bodyWidth = body.node().offsetWidth;
    const isMobile = bodyWidth < this.state.breakPoint;
    const width = this.base ? this.base.offsetWidth - (isMobile ? 10 : 80) : 300;
    return { width, bodyWidth };
  }

  redrawGraphic() {
    const {
      className,
      categoryNumbers,
      candidatesRender,
      candidates,
      officeSlug,
      party,
      maxNum,
    } = this.props;

    let { width, bodyWidth } = this.getContainerWidth(officeSlug);

    this.setState({ width: width, bodyWidth: bodyWidth });
    this.createGraphic(
      className,
      categoryNumbers,
      candidatesRender,
      candidates,
      officeSlug,
      party,
      maxNum
    );
  }

  componentWillUnmount() {
    window.addEventListener('resize', () => {
      this.redrawGraphic();
    });
  }

  render() {
    const { className, officeSlug, party } = this.props;
    const partySlug = party ? party.toLowerCase() : '';

    return (
      <div
        class={`category-bar-chart bar-chart-${className}-${officeSlug}-${partySlug}`}
      ></div>
    );
  }
}

export default MiniBarChart;
